import React, { useState, useRef } from 'react';

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function FileTypeBadge({ name }) {
  const ext = (name || '').split('.').pop().toLowerCase();
  const map = {
    pdf:  { label: 'PDF',  bg: '#fef2f2', color: '#dc2626' },
    doc:  { label: 'DOC',  bg: '#dbeafe', color: '#1e40af' },
    docx: { label: 'DOC',  bg: '#dbeafe', color: '#1e40af' },
    xlsx: { label: 'XLS',  bg: '#dcfce7', color: '#15803d' },
    xls:  { label: 'XLS',  bg: '#dcfce7', color: '#15803d' },
    msg:  { label: 'MSG',  bg: '#f3f4f6', color: '#374151' },
    eml:  { label: 'MSG',  bg: '#f3f4f6', color: '#374151' },
    jpg:  { label: 'IMG',  bg: '#d1fae5', color: '#065f46' },
    jpeg: { label: 'IMG',  bg: '#d1fae5', color: '#065f46' },
    png:  { label: 'IMG',  bg: '#d1fae5', color: '#065f46' },
  };
  const s = map[ext] || { label: 'FILE', bg: '#f3f4f6', color: '#374151' };
  return (
    <span style={{
      background: s.bg, color: s.color,
      padding: '2px 6px', borderRadius: '4px',
      fontSize: '10px', fontWeight: '700',
      letterSpacing: '0.04em', flexShrink: 0,
      fontFamily: 'monospace',
    }}>
      {s.label}
    </span>
  );
}

/**
 * Reusable drag-and-drop file selection zone.
 *
 * Props:
 *   onFilesSelected(files)  — called with array of newly selected/dropped Files
 *   files                   — current File[] to render in the list below the zone (managed by parent)
 *   onRemove(index)         — called when the user clicks × on a listed file
 *   accept                  — file input accept string
 *   multiple                — allow multiple files
 *   label                   — noun phrase used in drag-active message ("VQ documents", etc.)
 *   disabled                — disables the zone (greys it, blocks interaction)
 */
export function DropZone({
  onFilesSelected,
  files = [],
  onRemove,
  accept = '.pdf,.doc,.docx,.msg,.eml,.jpg,.jpeg,.png',
  multiple = true,
  label = 'files',
  disabled = false,
}) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  function emit(fileList) {
    if (disabled) return;
    const arr = Array.from(fileList);
    if (arr.length) onFilesSelected(arr);
  }

  const active = dragging && !disabled;

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        multiple={multiple}
        accept={accept}
        style={{ display: 'none' }}
        onChange={e => { emit(e.target.files); e.target.value = ''; }}
        disabled={disabled}
      />

      {/* ── Drop zone ── */}
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && !disabled && inputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); e.stopPropagation(); if (!disabled) setDragging(true); }}
        onDragEnter={e => { e.preventDefault(); e.stopPropagation(); if (!disabled) setDragging(true); }}
        onDragLeave={e => { e.preventDefault(); e.stopPropagation(); setDragging(false); }}
        onDrop={e => {
          e.preventDefault(); e.stopPropagation();
          setDragging(false);
          if (!disabled) emit(e.dataTransfer.files);
        }}
        style={{
          border: `2px dashed ${active ? '#111111' : disabled ? '#e5e7eb' : '#cbd5e0'}`,
          borderRadius: '8px',
          padding: '36px 24px',
          textAlign: 'center',
          cursor: disabled ? 'default' : 'pointer',
          background: active ? '#f0f0f0' : disabled ? '#fafafa' : '#f8f9fa',
          transition: 'border-color 0.15s, background-color 0.15s',
          outline: 'none',
          userSelect: 'none',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        {/* Upload arrow icon */}
        <svg
          width="40" height="40"
          viewBox="0 0 24 24" fill="none"
          stroke={active ? '#111111' : '#9ca3af'}
          strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
          style={{ display: 'block', margin: '0 auto 12px', transition: 'stroke 0.15s' }}
          aria-hidden="true"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>

        <p style={{
          margin: '0 0 4px', fontSize: '14px', fontWeight: '600',
          color: active ? '#111111' : '#374151',
          transition: 'color 0.15s',
        }}>
          {active
            ? `Drop ${label} here`
            : disabled
              ? 'Uploading…'
              : 'Drag & drop files here, or click to browse'}
        </p>
        <p style={{ margin: '0 0 6px', fontSize: '12px', color: '#9ca3af' }}>
          PDF · Word · Images · Email (.msg / .eml)
        </p>
        <p style={{ margin: 0, fontSize: '11px', color: '#b0bac5' }}>
          Max 20 MB per file
        </p>
      </div>

      {/* ── Selected files list ── */}
      {files.length > 0 && (
        <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {files.map((f, i) => (
            <div
              key={`${f.name}-${i}`}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '8px 12px', borderRadius: '6px',
                border: '1px solid #e5e7eb', background: '#ffffff',
              }}
            >
              <FileTypeBadge name={f.name} />
              <span style={{
                flex: 1, fontSize: '13px', color: '#111111',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {f.name}
              </span>
              <span style={{ fontSize: '12px', color: '#9ca3af', whiteSpace: 'nowrap', flexShrink: 0 }}>
                {formatBytes(f.size)}
              </span>
              {onRemove && (
                <button
                  type="button"
                  onClick={e => { e.stopPropagation(); onRemove(i); }}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#9ca3af', padding: '2px 5px', borderRadius: '3px',
                    fontSize: '18px', lineHeight: 1, flexShrink: 0,
                  }}
                  onMouseEnter={e => e.currentTarget.style.color = '#dc2626'}
                  onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}
                  aria-label={`Remove ${f.name}`}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
